import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { SearchBarProps, SuggestionItem } from "./types";
import SearchIcon from "../../assets/svg/search-icon";
import SearchSuggestionsList from "./SearchSuggestionsList";
import { useDebounce } from "../../hooks/useDebounce";
import CrossIcon from "../../assets/svg/v1/cross.icon";

const SearchBar: React.FC<SearchBarProps> = ({
  id = "search",
  name = "search",
  placeholder = "Search...",
  defaultValue = "",
  disabled = false,
  suggestions = [],
  noSuggestionsText = "No suggestions found",
  loadingText = "",
  loading = false,
  debounceMs = 500,
  onSearch,
  onSelect,
  onClear,
  className = "",
  containerClassName = "",
  suggestionsClassName = "",
  suggestionItemClassName = "",
  ariaLabel = "Search input",
  ariaDescribedBy,
  allowClear = true,
  closeOnSelect = true,
  searchIcon,
  clearIcon,
  showSearchIcon = true,
  minCharsToSearch = 1,
  enableApiSearch = false,
  onApiSearch,
  initialSuggestions = [],
  apiSearchDebounceMs = 500,
  clearSearchValue = false,
  setClearSearchValue,
  showSuggestionDropdown = true,
  enableInfiniteScroll = false,
  hasMoreDataToLoad,
}) => {
  const [value, setValue] = useState(defaultValue);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showSuggestionsList, setShowSuggestionsList] = useState(false);
  const [isApiSearching, setIsApiSearching] = useState(false);
  const [apiSuggestions, setApiSuggestions] = useState<SuggestionItem[]>([]);
  const [isPendingSearch, setIsPendingSearch] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isClearingRef = useRef(false);
  const apiSearchTimeoutRef = useRef<NodeJS.Timeout>();

  const debouncedValue = useDebounce(value, debounceMs);

  const handleApiSearch = async (query: string) => {
    if (!enableApiSearch || !onApiSearch || !query.trim()) {
      setApiSuggestions([]);
      return;
    }

    const wasFocused = document.activeElement === inputRef.current;
    setIsApiSearching(true);

    try {
      const results = await onApiSearch(query);
      setApiSuggestions(results);
    } catch (error) {
      console.error("API search failed:", error);
      setApiSuggestions([]);
    } finally {
      setIsApiSearching(false);

      if (wasFocused && inputRef.current) {
        requestAnimationFrame(() => {
          inputRef.current?.focus();
        });
      }
    }
  };

  const handleApiSearchLoadMore = useCallback(async () => {
    try {
      if (
        !enableInfiniteScroll ||
        !enableApiSearch ||
        !onApiSearch ||
        !value.trim() ||
        !hasMoreDataToLoad
      ) {
        return;
      }
      setIsLoadingMore(true);
      const results = await onApiSearch(value.trim(), apiSuggestions.length);
      setApiSuggestions([...apiSuggestions, ...results]);
      setIsLoadingMore(false);
    } catch (error) {
      console.error("API search load more failed:", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    enableInfiniteScroll,
    enableApiSearch,
    onApiSearch,
    value,
    hasMoreDataToLoad,
    apiSuggestions,
  ]);

  useEffect(() => {
    if (apiSearchTimeoutRef.current) {
      clearTimeout(apiSearchTimeoutRef.current);
    }

    if (enableApiSearch && value.trim().length >= minCharsToSearch) {
      setIsPendingSearch(true);
      apiSearchTimeoutRef.current = setTimeout(() => {
        setIsPendingSearch(false);
        handleApiSearch(value.trim());
      }, apiSearchDebounceMs);
    } else if (enableApiSearch && value.trim().length === 0) {
      setIsPendingSearch(false);
      setApiSuggestions([]);
    }

    return () => {
      if (apiSearchTimeoutRef.current) {
        clearTimeout(apiSearchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, enableApiSearch, apiSearchDebounceMs, minCharsToSearch]);

  useEffect(() => {
    if (clearSearchValue) {
      setValue("");
      setClearSearchValue?.(false);
    }
  }, [clearSearchValue, setClearSearchValue]);

  useEffect(() => {
    if (!enableApiSearch && debouncedValue.trim().length >= minCharsToSearch) {
      onSearch?.(debouncedValue.trim());
    } else if (!enableApiSearch && debouncedValue.trim().length === 0) {
      onSearch?.("");
    }
  }, [debouncedValue, onSearch, minCharsToSearch, enableApiSearch]);

  const effectiveSuggestions = useMemo(() => {
    if (enableApiSearch) {
      if (value.trim()) {
        return apiSuggestions;
      } else {
        return initialSuggestions;
      }
    }
    return suggestions;
  }, [enableApiSearch, value, apiSuggestions, initialSuggestions, suggestions]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setValue(newValue);
      setSelectedIndex(-1);

      if (enableApiSearch) {
        if (newValue.trim().length >= minCharsToSearch) {
          setShowSuggestionsList(true);
        } else if (newValue.trim().length === 0) {
          setShowSuggestionsList(initialSuggestions.length > 0);
          onClear?.();
        } else {
          setShowSuggestionsList(false);
        }
      } else {
        if (newValue.trim().length >= minCharsToSearch) {
          setShowSuggestionsList(true);
        } else {
          setShowSuggestionsList(false);
        }
      }
    },
    [minCharsToSearch, enableApiSearch, initialSuggestions, onClear]
  );

  const handleFocus = useCallback(() => {
    if (
      !isClearingRef.current &&
      enableApiSearch &&
      value.trim().length === 0 &&
      initialSuggestions.length > 0
    ) {
      setShowSuggestionsList(true);
    }
  }, [enableApiSearch, value, initialSuggestions]);

  const handleSuggestionSelect = useCallback(
    (suggestion: string, index: number) => {
      setValue(suggestion);
      setShowSuggestionsList(false);
      setSelectedIndex(-1);

      const suggestionItem =
        typeof effectiveSuggestions[index] === "object"
          ? (effectiveSuggestions[index] as SuggestionItem)
          : undefined;
      onSelect?.(suggestion, suggestionItem);

      if (closeOnSelect) {
        inputRef.current?.blur();
      }
    },
    [effectiveSuggestions, onSelect, closeOnSelect]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current && !containerRef.current.contains(target)) {
        setShowSuggestionsList(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleClear = useCallback(() => {
    isClearingRef.current = true;
    setValue("");
    setSelectedIndex(-1);
    setShowSuggestionsList(false);
    setApiSuggestions([]);
    setIsPendingSearch(false);
    onClear?.();
    onSearch?.("");

    setTimeout(() => {
      isClearingRef.current = false;
      inputRef.current?.focus();
    }, 0);
  }, [onClear, onSearch]);

  const listboxId = `${id}-listbox`;
  const suggestionId = (index: number) => `${id}-suggestion-${index}`;

  const shouldShowSuggestions = Boolean(
    showSuggestionsList &&
      (loading ||
        isApiSearching ||
        value.trim().length >= minCharsToSearch ||
        (enableApiSearch && initialSuggestions.length > 0))
  );

  return (
    <div className={`relative ${containerClassName}`} ref={containerRef}>
      <div
        className={`w-full flex items-center gap-1 px-[10px] py-[9px] rounded-[8px] border border-border-gray dark:border-border-gray-dk bg-popper-bg dark:bg-popper-bg-dk backdrop-blur-[10px] focus:outline-none transition-all ${
          disabled ? "opacity-50 " : ""
        } ${className}`}
      >
        <div className="shrink-0 text-heading-text dark:text-heading-text-dk">
          {showSearchIcon ? (
            searchIcon ? (
              searchIcon
            ) : (
              <SearchIcon className="w-[18px] h-[18px]" />
            )
          ) : null}
        </div>

        <input
          ref={inputRef}
          id={id}
          name={name}
          type="text"
          placeholder={placeholder}
          value={value}
          disabled={disabled}
          onChange={handleInputChange}
          onFocus={handleFocus}
          className="flex-1 min-w-0 bg-transparent border-none outline-none font-normal text-md text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder placeholder:font-normal placeholder:text-md"
          role="combobox"
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          aria-expanded={shouldShowSuggestions}
          aria-owns={shouldShowSuggestions ? listboxId : undefined}
          aria-activedescendant={
            selectedIndex >= 0 ? suggestionId(selectedIndex) : undefined
          }
          aria-autocomplete="list"
          autoComplete="off"
        />

        {allowClear && value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="shrink-0 text-heading-text dark:text-heading-text-dk"
            aria-label="Clear search"
          >
            {clearIcon || <CrossIcon className="w-4 h-4" />}
          </button>
        )}
      </div>

      {shouldShowSuggestions && showSuggestionDropdown && (
        <SearchSuggestionsList
          listboxId={listboxId}
          suggestionId={suggestionId}
          suggestionsClassName={suggestionsClassName}
          filteredSuggestions={effectiveSuggestions}
          selectedIndex={selectedIndex}
          suggestionItemClassName={suggestionItemClassName}
          onSuggestionSelect={handleSuggestionSelect}
          noSuggestionsText={noSuggestionsText}
          loadingText={loadingText}
          isLoading={
            (loading || isApiSearching || isPendingSearch) &&
            value.trim().length > 0
          }
          isLoadingMore={isLoadingMore}
          hasMoreSuggestionsToLoad={hasMoreDataToLoad}
          handleApiSearchLoadMore={handleApiSearchLoadMore}
          enableApiSearch={enableApiSearch}
        />
      )}
    </div>
  );
};

export default SearchBar;
